/**
 * Store de eficiencia reproductiva de cría (FRONTEND_INTEGRATION.md §3, §9, §11, §12).
 *
 * - Habilitación: `GET /cria/campanias?limite=1`. 404 `funcion_no_habilitada` o 403 ocultan
 *   la sección; cualquier 404 `funcion_no_habilitada` posterior también.
 * - Cambio de establecimiento: `reiniciar` sube la generación, cancela lo pendiente y vacía
 *   la caché; las respuestas de una generación anterior se descartan.
 * - Caché opcional del cliente: la clave incluye establecimiento, usuario/rol, recurso,
 *   parámetros y la fecha de corte resuelta. Las mutaciones invalidan según §11.2.
 *   No hay actualizaciones optimistas de agregados.
 */
import criaEficienciaApi from "@/services/cria/criaEficienciaApi";
import { esCancelacion, esFuncionNoHabilitada } from "@/utils/cria/criaErrores";
import { fechaHoyLocal } from "@/utils/cria/criaFormato";
import { enqueueOp } from "@/services/offlineQueue";

export const HABILITACION = Object.freeze({
  DESCONOCIDA: "desconocida",
  VERIFICANDO: "verificando",
  HABILITADA: "habilitada",
  NO_HABILITADA: "no_habilitada",
  SIN_PERMISO: "sin_permiso",
  ERROR: "error",
});

/** Vida de una entrada de caché (ms). La caché acelera volver atrás; no reemplaza recargar. */
export const CACHE_TTL_MS = 60 * 1000;

/** Respuesta descartada: era de otra generación (otro establecimiento) o se canceló. */
export const DESCARTADA = Object.freeze({ descartada: true });

// Fuera del estado reactivo: no se serializan ni disparan renders.
let controlador = new AbortController();
const cache = new Map();

/** Orden estable de claves para que `{a,b}` y `{b,a}` generen la misma clave de caché. */
function normalizarParams(params = {}) {
  const salida = {};
  Object.keys(params || {})
    .sort()
    .forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null || v === "") return;
      salida[k] = Array.isArray(v)
        ? [...v]
            .map(String)
            .sort((a, b) => a.localeCompare(b, "en", { numeric: true }))
        : v;
    });
  return salida;
}

export function claveCache({ tamboId, usuario, recurso, params }) {
  const p = normalizarParams(params);
  // Sin fecha_corte el backend usa "hoy": la clave lleva la fecha resuelta para no
  // reutilizar la respuesta al día siguiente.
  if (
    p.fecha_corte === undefined &&
    recurso !== "campanias" &&
    recurso !== "campania"
  ) {
    p.__corte_hoy = fechaHoyLocal();
  }
  return JSON.stringify([tamboId, usuario, recurso, p]);
}

function contexto(rootGetters) {
  return {
    tamboId: rootGetters["userTamboStore/currentTamboId"] ?? null,
    usuario: `${rootGetters["userTamboStore/userId"] ?? ""}:${
      rootGetters["userTamboStore/currentRole"] ?? ""
    }`,
  };
}

/**
 * Etiquetas de invalidación de cada recurso. `campania:<id>` agrupa detalle, vientres,
 * superficies e indicadores de una campaña; `animal:<id>` la ficha.
 */
function etiquetasDe(recurso, params = {}) {
  const tags = [recurso];
  if (params.campania_id !== undefined)
    tags.push(`campania:${params.campania_id}`);
  if (params.animal_id !== undefined) tags.push(`animal:${params.animal_id}`);
  return tags;
}

export function invalidarCache(predicado) {
  for (const [clave, entrada] of cache.entries()) {
    if (predicado(entrada.tags)) cache.delete(clave);
  }
}

const porEtiqueta =
  (...buscadas) =>
  (tags) =>
    tags.some((t) => buscadas.includes(t));

/** Reglas de §11.2. Recibe el tipo de mutación y los IDs afectados. */
export function invalidarPorMutacion(
  tipo,
  { campaniaId, animalIds = [] } = {}
) {
  const deCampania = campaniaId !== undefined ? [`campania:${campaniaId}`] : [];
  const deAnimales = animalIds
    .filter((x) => x != null)
    .map((id) => `animal:${id}`);
  switch (tipo) {
    case "campania_editada":
      invalidarCache(porEtiqueta("campanias", ...deCampania));
      break;
    case "campania_cerrada":
      invalidarCache(
        porEtiqueta("campanias", ...deCampania, "rankings", "comparacion")
      );
      break;
    case "vientre_modificado":
      invalidarCache(
        porEtiqueta(
          "campanias",
          ...deCampania,
          "rankings",
          "comparacion",
          ...deAnimales
        )
      );
      break;
    case "superficie_agregada":
      invalidarCache(porEtiqueta(...deCampania));
      break;
    case "evento_reproductivo":
    case "peso_destete":
      invalidarCache(
        porEtiqueta(
          "indicadores",
          "rankings",
          "comparacion",
          "calidad",
          "eficiencia",
          ...deAnimales
        )
      );
      break;
    default:
      cache.clear();
  }
}

export function vaciarCache() {
  cache.clear();
}

const state = () => ({
  habilitacion: HABILITACION.DESCONOCIDA,
  habilitacionTamboId: null,
  generacion: 0,
});

const getters = {
  habilitacion: (s) => s.habilitacion,
  criaHabilitada: (s) => s.habilitacion === HABILITACION.HABILITADA,
  seccionVisible: (s) => s.habilitacion === HABILITACION.HABILITADA,
  generacion: (s) => s.generacion,
};

const mutations = {
  SET_HABILITACION(s, { valor, tamboId }) {
    s.habilitacion = valor;
    s.habilitacionTamboId = tamboId ?? null;
  },
  INCREMENTAR_GENERACION(s) {
    s.generacion += 1;
  },
};

/** Ante 404 `funcion_no_habilitada` en cualquier pantalla: vaciar caché y ocultar sección. */
function manejarNoHabilitada(commit, rootGetters, error) {
  if (esFuncionNoHabilitada(error)) {
    cache.clear();
    commit("SET_HABILITACION", {
      valor: HABILITACION.NO_HABILITADA,
      tamboId: contexto(rootGetters).tamboId,
    });
  }
}

/**
 * Lectura con generación y caché. Devuelve `DESCARTADA` si la respuesta llegó después de
 * un cambio de establecimiento o si el pedido se canceló.
 */
async function leer(
  { state: s, commit, rootGetters },
  {
    recurso,
    params = {},
    clave = {},
    fetcher,
    forzar = false,
    usarCache = true,
  }
) {
  const ctx = contexto(rootGetters);
  const k = claveCache({ ...ctx, recurso, params: { ...clave, ...params } });
  const ahora = Date.now();
  if (usarCache && !forzar) {
    const entrada = cache.get(k);
    if (entrada && ahora - entrada.en < CACHE_TTL_MS) return entrada.datos;
  }
  const mia = s.generacion;
  try {
    const datos = await fetcher({ signal: controlador.signal });
    if (mia !== s.generacion) return DESCARTADA;
    if (usarCache) {
      cache.set(k, { datos, en: ahora, tags: etiquetasDe(recurso, clave) });
    }
    return datos;
  } catch (error) {
    if (mia !== s.generacion || esCancelacion(error)) return DESCARTADA;
    manejarNoHabilitada(commit, rootGetters, error);
    throw error;
  }
}

/** Escritura con generación. Las respuestas tardías de otro establecimiento se descartan. */
async function escribir({ state: s, commit, rootGetters }, fetcher) {
  const mia = s.generacion;
  try {
    const datos = await fetcher({ signal: controlador.signal });
    if (mia !== s.generacion) return DESCARTADA;
    return datos;
  } catch (error) {
    if (mia !== s.generacion || esCancelacion(error)) return DESCARTADA;
    manejarNoHabilitada(commit, rootGetters, error);
    throw error;
  }
}

const actions = {
  async verificarHabilitacion(ctx, { forzar = false } = {}) {
    const { state: s, commit, rootGetters } = ctx;
    const { tamboId } = contexto(rootGetters);
    if (
      !forzar &&
      s.habilitacionTamboId === tamboId &&
      [
        HABILITACION.HABILITADA,
        HABILITACION.NO_HABILITADA,
        HABILITACION.SIN_PERMISO,
      ].includes(s.habilitacion)
    ) {
      return s.habilitacion;
    }
    const mia = s.generacion;
    commit("SET_HABILITACION", { valor: HABILITACION.VERIFICANDO, tamboId });
    try {
      await criaEficienciaApi.listarCampanias(
        { limite: 1 },
        { signal: controlador.signal }
      );
      if (mia !== s.generacion) return s.habilitacion;
      commit("SET_HABILITACION", { valor: HABILITACION.HABILITADA, tamboId });
    } catch (error) {
      if (mia !== s.generacion || esCancelacion(error)) return s.habilitacion;
      const status = error?.response?.status;
      let valor = HABILITACION.ERROR;
      if (esFuncionNoHabilitada(error)) valor = HABILITACION.NO_HABILITADA;
      else if (status === 403) valor = HABILITACION.SIN_PERMISO;
      commit("SET_HABILITACION", { valor, tamboId });
    }
    return s.habilitacion;
  },

  /**
   * Mutaciones hechas por rutas fuera de este store (venta, muerte, tacto masivo, etc.):
   * invalida lo mismo que un evento reproductivo (§11.2).
   */
  notificarEventoExterno(_, { animalIds = [] } = {}) {
    invalidarPorMutacion("evento_reproductivo", { animalIds });
  },

  /** Llamado al cambiar de establecimiento o cerrar sesión (§9). */
  reiniciar({ commit }) {
    commit("INCREMENTAR_GENERACION");
    controlador.abort();
    controlador = new AbortController();
    cache.clear();
    commit("SET_HABILITACION", {
      valor: HABILITACION.DESCONOCIDA,
      tamboId: null,
    });
  },

  // ── Lecturas ──
  cargarCampanias(ctx, { params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "campanias",
      params,
      forzar,
      fetcher: (o) => criaEficienciaApi.listarCampanias(params, o),
    });
  },
  cargarCampania(ctx, { campaniaId, forzar } = {}) {
    return leer(ctx, {
      recurso: "campania",
      clave: { campania_id: campaniaId },
      forzar,
      fetcher: (o) => criaEficienciaApi.obtenerCampania(campaniaId, o),
    });
  },
  cargarIndicadores(ctx, { campaniaId, params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "indicadores",
      clave: { campania_id: campaniaId },
      params,
      forzar,
      fetcher: (o) =>
        criaEficienciaApi.obtenerIndicadores(campaniaId, params, o),
    });
  },
  cargarVientres(ctx, { campaniaId, params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "vientres",
      clave: { campania_id: campaniaId },
      params,
      forzar,
      fetcher: (o) => criaEficienciaApi.listarVientres(campaniaId, params, o),
    });
  },
  cargarSuperficies(ctx, { campaniaId, forzar } = {}) {
    return leer(ctx, {
      recurso: "superficies",
      clave: { campania_id: campaniaId },
      forzar,
      fetcher: (o) => criaEficienciaApi.listarSuperficies(campaniaId, o),
    });
  },
  cargarEficienciaAnimal(ctx, { animalId, params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "eficiencia",
      clave: { animal_id: animalId },
      params,
      forzar,
      fetcher: (o) =>
        criaEficienciaApi.obtenerEficienciaAnimal(animalId, params, o),
    });
  },
  cargarRanking(ctx, { params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "rankings",
      params,
      forzar,
      fetcher: (o) => criaEficienciaApi.obtenerRanking(params, o),
    });
  },
  cargarComparacion(ctx, { params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "comparacion",
      params,
      forzar,
      fetcher: (o) => criaEficienciaApi.obtenerComparacion(params, o),
    });
  },
  cargarCalidad(ctx, { params = {}, forzar } = {}) {
    return leer(ctx, {
      recurso: "calidad",
      params,
      forzar,
      fetcher: (o) => criaEficienciaApi.obtenerCalidadDatos(params, o),
    });
  },

  // ── Mutaciones (invalidan según §11.2 tras el éxito) ──
  async crearCampania(ctx, cuerpo) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.crearCampania(cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("campania_editada", { campaniaId: r?.id });
    return r;
  },
  async editarCampania(ctx, { campaniaId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.editarCampania(campaniaId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("campania_editada", { campaniaId });
    return r;
  },
  async cerrarCampania(ctx, { campaniaId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.cerrarCampania(campaniaId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("campania_cerrada", { campaniaId });
    return r;
  },
  async reabrirCampania(ctx, { campaniaId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.reabrirCampania(campaniaId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("campania_cerrada", { campaniaId });
    return r;
  },
  async inscribirVientres(ctx, { campaniaId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.inscribirVientres(campaniaId, cuerpo, o)
    );
    if (r !== DESCARTADA) {
      const animalIds = (cuerpo?.vientres || []).map((v) => v?.animal_id);
      invalidarPorMutacion("vientre_modificado", { campaniaId, animalIds });
    }
    return r;
  },
  async corregirVientre(ctx, { campaniaId, animalId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.corregirVientre(campaniaId, animalId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("vientre_modificado", {
        campaniaId,
        animalIds: [animalId],
      });
    return r;
  },
  async agregarSuperficie(ctx, { campaniaId, cuerpo }) {
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.agregarSuperficie(campaniaId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("superficie_agregada", { campaniaId });
    return r;
  },
  async registrarPesoDestete(ctx, { animalId, madreId, cuerpo }) {
    if (!navigator.onLine) {
      await enqueueOp(
        "registros_peso",
        "create",
        cuerpo,
        `/cria/animales/${animalId}/peso-destete`,
        "POST"
      );
      return { detail: "Peso al destete guardado offline", _offline: true };
    }
    const r = await escribir(ctx, (o) =>
      criaEficienciaApi.registrarPesoDestete(animalId, cuerpo, o)
    );
    if (r !== DESCARTADA)
      invalidarPorMutacion("peso_destete", { animalIds: [animalId, madreId] });
    return r;
  },
  /**
   * Capturas (servicio, tacto, parto, destete). `tipo`: servicio | tacto | parto | destete.
   * Invalida ficha del animal (y de la madre), indicadores, rankings, comparación y calidad.
   */
  async registrarEvento(ctx, { tipo, animalId, madreId, cuerpo }) {
    if (!navigator.onLine) {
      const endpoints = {
        servicio: `/cria/registrar-servicio/${animalId}`,
        tacto: `/cria/registrar-tacto/${animalId}`,
        parto: `/cria/registrar-parto/${animalId}`,
        destete: `/feedlot/destete/${animalId}`,
      };
      const tablas = {
        servicio: "inseminaciones",
        tacto: "tactos",
        parto: "animales",
        destete: "destetes",
      };
      if (endpoints[tipo]) {
        await enqueueOp(
          tablas[tipo] || "animales",
          "create",
          cuerpo,
          endpoints[tipo],
          "POST"
        );
        return {
          detail: `${tipo.charAt(0).toUpperCase() + tipo.slice(1)} registrado offline`,
          _offline: true,
        };
      }
    }
    const llamadas = {
      servicio: criaEficienciaApi.registrarServicio,
      tacto: criaEficienciaApi.registrarTacto,
      parto: criaEficienciaApi.registrarParto,
      destete: criaEficienciaApi.registrarDestete,
    };
    const llamada = llamadas[tipo];
    if (!llamada) throw new Error(`Tipo de evento desconocido: ${tipo}`);
    const r = await escribir(ctx, (o) => llamada(animalId, cuerpo, o));
    if (r !== DESCARTADA)
      invalidarPorMutacion("evento_reproductivo", {
        animalIds: [animalId, madreId],
      });
    return r;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
